import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationExtras, Params, Router } from '@angular/router';
import { BasicError } from 'projects/shared/lib/error/basic-error';
import { ICartCreationDialogService } from 'shared/services/ui/cart-creation-dialog/cart-creation-dialog-service.abstract';
import { ICartService, ICartServiceToken } from 'projects/shared/lib/services/data/cart/cart-service.interface';
import { IAuthService, IAuthServiceToken } from 'projects/shared/lib/services/infrastructure/authentication/auth-service.interface';
import { LoggerService } from 'projects/shared/lib/services/infrastructure/logger/logger.service';
import { ServiceLocator } from 'projects/shared/lib/services/infrastructure/service-locator.service';
import { EShoppingCartGetQueryType, ShoppingCartDto } from 'projects/shared/lib/services/INTEGRA_Shop_Client';
import { ISearchService, ISearchServiceToken } from 'projects/shared/lib/services/ui/search/search-service.interface';
import { firstValueFrom, Subscription } from 'rxjs';
import { ISidebarCartService, ISidebarCartServiceToken } from 'shared/services/ui/sidebar/cart/sidebar-cart-service.interface';

@Component({
  selector: 'app-sticky-footer-mobile',
  templateUrl: './sticky-footer-mobile.component.html',
  standalone: false
})
export class StickyFooterMobileComponent implements OnInit, OnDestroy {
  private logger: LoggerService;
  private readonly authService: IAuthService;
  private readonly cartService: ICartService;
  private readonly sidebarCartService: ISidebarCartService = inject(ISidebarCartServiceToken);
  private readonly searchService: ISearchService;
  private router: Router;

  private authenticationSubscription: Subscription | undefined;
  private cartSubscription: Subscription | undefined;
  private sidebarShoppingCartVisibilitySubscription: Subscription | undefined;
  private searchTermSubscriptionSubscription: Subscription | undefined;

  public shouldDisplayStickyFooterSearch: boolean | undefined;

  public isSearchFocused: boolean | undefined;
  public searchTerm: string | undefined;

  public isAuthenticated: boolean | undefined;
  public cart: ShoppingCartDto | undefined;

  private innerHeight: number;
  public keyboardHeight: number;
  public keyboardIsActive: boolean;

  @ViewChild('searchBarInput') searchBarInput: ElementRef | undefined;

  public constructor() {
    this.logger = ServiceLocator.injector.get(LoggerService);
    this.authService = inject(IAuthServiceToken);
    this.cartService = inject(ICartServiceToken);
    this.searchService = inject(ISearchServiceToken);
    this.router = ServiceLocator.injector.get(Router);
    this.keyboardIsActive = false;
    this.keyboardHeight = 0;
    this.innerHeight = window.innerHeight;
  }

  public ngOnInit(): void {
    this.authenticationSubscriptionInit();
    this.cartSubscriptionInit();
    this.sidebarShoppingCartVisibilitySubscriptionInit();
    this.searchTermSubscriptionSubscriptionInit();
  }

  public ngOnDestroy(): void {
    this.authenticationSubscription?.unsubscribe();
    this.cartSubscription?.unsubscribe();
    this.sidebarShoppingCartVisibilitySubscription?.unsubscribe();
    this.searchTermSubscriptionSubscription?.unsubscribe();
  }

  private authenticationSubscriptionInit() {
    if (!this.authenticationSubscription) {
      this.authenticationSubscription = this.authService.authenticatedGet().subscribe(
        () => {
          this.isAuthenticated = this.authService.isAuthenticated();
        }
      );
    }
  }

  public searchOnClick() {
    if (this.innerHeight < window.innerHeight) {
      this.keyboardIsActive = true;
      this.keyboardHeight = window.innerHeight - this.innerHeight;
    }
    if (this.innerHeight > window.innerHeight) {
      this.keyboardIsActive = false;
      this.keyboardHeight = 0;
    }
    this.innerHeight = window.innerHeight;
  }

  private cartSubscriptionInit() {
    if (!this.cartSubscription) {
      this.cartSubscription = this.cartService.cartCurrentChangedGet().subscribe(
        (cartCurrent) => {
          this.cart = cartCurrent;
        }
      );
    }
  }

  private sidebarShoppingCartVisibilitySubscriptionInit() {
    if (!this.sidebarShoppingCartVisibilitySubscription) {
      this.sidebarShoppingCartVisibilitySubscription = this.sidebarCartService.sidebarShoppingCartChangedGet().subscribe(
        (sidebarShoppingCartDesiredVisibility) => {
          this.shouldDisplayStickyFooterSearch = sidebarShoppingCartDesiredVisibility;
        }
      );
    }
  }

  private searchTermSubscriptionSubscriptionInit() {
    if (!this.searchTermSubscriptionSubscription) {
      this.searchTermSubscriptionSubscription = this.searchService.searchStateChangedGet().subscribe(
        (searchState) => {
          this.searchTerm = searchState.searchTerm;
        }
      );
    }
  }

  searchInputKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case "Enter":
        this.search();
        break;
    }
  }

  public search() {
    this.searchTerm = this.searchTerm?.trim();
    this.searchBarInput?.nativeElement.blur();
    let queryParams: Params = {
      searchText: this.searchTerm,
    };
    let navigationExtras: NavigationExtras = { queryParams: queryParams };
    this.router.navigate(['/product', 'search'], navigationExtras);
  }

  public async createCartClickAsync(cart?: ShoppingCartDto | undefined) {
    if (cart == undefined) {
      // try looking for an "old" one
      await firstValueFrom(this.cartService.cartGet(EShoppingCartGetQueryType.GetLatestOrCreateNew, false)).then((result) => {
        cart = result.shoppingCart;
      }).catch((error) => {
        this.logger.error(error, this.constructor.name);
        let errorMessage = $localize`:@@StickyFooterCreateCartErrorMessage:Could not create cart`;
        throw new BasicError(errorMessage, JSON.parse(error.response).errorCode);
      });
    }
    ServiceLocator.injector.get(ICartCreationDialogService).cartCreationDialogShow(
      () => { },
      cart
    );
  }

}
